<template>
    <main class="timesheet-settings">
        <header>
            <div>
                <b-button variant="link" class="p-0 mb-2" to="/timesheets">
                    <i class="fas fa-arrow-left mr-1" aria-hidden="true"></i>
                    К табелям
                </b-button>
                <h1>Настройки табелей</h1>
                <p>Правила времени и участники процесса</p>
            </div>
            <b-button v-if="hasLoaded" variant="primary" :disabled="saving" @click="save">
                <b-spinner v-if="saving" small></b-spinner>
                <i v-else class="fas fa-save mr-1" aria-hidden="true"></i>
                Сохранить
            </b-button>
        </header>

        <b-alert v-if="accessDenied" show variant="warning">
            Нет доступа к настройкам модуля табелей.
        </b-alert>
        <b-alert v-if="errorMessage" show dismissible variant="danger" @dismissed="errorMessage = ''">
            {{ errorMessage }}
        </b-alert>
        <b-alert v-if="successMessage" show dismissible variant="success" @dismissed="successMessage = ''">
            {{ successMessage }}
        </b-alert>
        <div v-if="loading" class="timesheet-settings__loading">
            <b-spinner></b-spinner>
            Загрузка настроек…
        </div>

        <b-tabs v-else-if="hasLoaded" pills card>
            <b-tab title="Карточки сотрудников" lazy>
                <TimesheetEmployeeCardSettings v-model="form.policy_config.employee_card_responsibles"
                    :users="participantUsers" :departments="participantDepartments" />
            </b-tab>
            <b-tab title="Объекты базы" lazy><TimesheetPayrollBases :objects="objects" /></b-tab>
            <b-tab title="Сроки подачи">
                <TimesheetSubmissionSettings v-model="form.policy_config.submission_deadline" />
            </b-tab>
            <b-tab title="Исключения из комплекта">
                <TimesheetPackageExclusions v-model="form.policy_config.payroll_package_exclusions" :objects="objects" />
            </b-tab>
            <b-tab title="Запуск" active>
                <section class="timesheet-settings__section">
                    <h2>Начало работы с табелями</h2>
                    <label>
                        <span>Дата включения для всех объектов</span>
                        <b-form-input
                            v-model="form.rollout_config.full_timesheet_start_date"
                            type="date"
                        ></b-form-input>
                    </label>
                    <b-alert show variant="secondary">
                        Новые табели создаются начиная с этой даты. За предыдущие месяцы доступна перенесённая история.
                    </b-alert>
                </section>
            </b-tab>

            <b-tab title="Правила времени">
                <section class="timesheet-settings__section timesheet-settings__grid">
                    <label>
                        <span>Шаг ввода, минут</span>
                        <b-form-input
                            v-model.number="form.policy_config.time_step_minutes"
                            type="number"
                            min="1"
                            max="60"
                        ></b-form-input>
                    </label>
                    <label>
                        <span>Нормальная продолжительность дня, минут</span>
                        <b-form-input
                            v-model.number="form.policy_config.normal_daily_minutes"
                            type="number"
                            min="1"
                            max="1440"
                        ></b-form-input>
                    </label>
                    <label>
                        <span>Абсолютный максимум за день, минут</span>
                        <b-form-input
                            v-model.number="form.policy_config.maximum_daily_minutes"
                            type="number"
                            min="60"
                            max="1440"
                        ></b-form-input>
                    </label>
                    <b-form-checkbox v-model="form.policy_config.allow_unassigned_employee">
                        Разрешать табелирование сотрудника без назначения на объект
                    </b-form-checkbox>
                    <b-form-checkbox v-model="form.policy_config.allow_unresolved_person">
                        Разрешать добавление человека без карточки сотрудника
                    </b-form-checkbox>
                </section>
                <section class="timesheet-settings__section">
                    <h2>Кадровые отсутствия</h2>
                    <p class="timesheet-settings__hint">
                        Эти соответствия используются только для предложений. Система не
                        перезаписывает табель без подтверждения пользователя.
                    </p>
                    <div class="timesheet-settings__absence-mapping">
                        <article
                            v-for="absenceType in absenceTypes"
                            :key="absenceType.key"
                        >
                            <b-form-checkbox
                                v-model="form.policy_config.absence_code_mapping[absenceType.key].enabled"
                            >
                                {{ absenceType.label }}
                            </b-form-checkbox>
                            <b-form-select
                                v-model="form.policy_config.absence_code_mapping[absenceType.key].code"
                                :options="absenceCodeOptions"
                                :disabled="!form.policy_config.absence_code_mapping[absenceType.key].enabled"
                                :aria-label="`Код табеля: ${absenceType.label}`"
                            ></b-form-select>
                        </article>
                    </div>
                </section>
                <section class="timesheet-settings__section">
                    <h2>ИТР</h2>
                    <p class="timesheet-settings__hint">
                        Сотрудник попадает в отдельный блок ИТР, если здесь выбран его основной
                        отдел карточки или один из отделов учётной записи. Должность и ручная
                        отметка строки на это не влияют.
                    </p>
                    <div class="timesheet-settings__department-list">
                        <b-form-checkbox
                            v-for="department in participantDepartments"
                            :key="`itr-department-${department.value}`"
                            v-model="form.policy_config.itr_department_ids"
                            :value="department.value"
                        >
                            {{ department.text }}
                        </b-form-checkbox>
                        <p v-if="!participantDepartments.length">
                            Действующие отделы не найдены.
                        </p>
                    </div>
                </section>
                <section class="timesheet-settings__section">
                    <h2>Расчёт отработанных дней</h2>
                    <div class="timesheet-settings__grid">
                        <label>
                            <span>Минимум минут для полного дня</span>
                            <b-form-input
                                v-model.number="form.policy_config.day_counting.full_day_minimum_minutes"
                                type="number"
                                min="1"
                                max="1440"
                            ></b-form-input>
                        </label>
                        <label>
                            <span>Вес короткой смены</span>
                            <b-form-input
                                v-model.number="form.policy_config.day_counting.partial_day_value"
                                type="number"
                                min="0"
                                max="1"
                                step="0.1"
                            ></b-form-input>
                        </label>
                        <label>
                            <span>Вес дороги без рабочих часов</span>
                            <b-form-input
                                v-model.number="form.policy_config.day_counting.travel_only_day_value"
                                type="number"
                                min="0"
                                max="1"
                                step="0.1"
                            ></b-form-input>
                        </label>
                        <label>
                            <span>Коды дороги, дающие дни</span>
                            <b-form-input
                                v-model.trim="travelDayCodesText"
                                placeholder="Д"
                                @change="applyTravelDayCodes"
                            ></b-form-input>
                        </label>
                    </div>
                    <b-alert show variant="secondary">
                        Текущая логика старого табеля: от 5 часов — 1 день, короткая смена и дорога без часов — 0,5 дня.
                    </b-alert>
                </section>
                <section class="timesheet-settings__section">
                    <h2>КТУ и характеристики</h2>
                    <b-form-checkbox v-model="form.policy_config.text_quality_enabled" switch class="mb-2">
                        Проверять содержательность характеристик и пояснений КТУ
                    </b-form-checkbox>
                    <p class="timesheet-settings__hint">По умолчанию выключено. При включении точки, заглушки и общие отписки блокируют новую отправку табеля.
                        Черновики сохраняются; ранее отправленные и утверждённые табели не пересматриваются. Причины сменных отчётов включаются отдельно в настройках отчётов.</p>
                    <b-alert show variant="primary">
                        <strong>Единая шкала: 0–5, шаг 0,5.</strong>
                        Оценка 4 нейтральна и означает нормальную работу. Шкала одинакова для всех компаний и не меняется в настройках.
                    </b-alert>
                    <p class="timesheet-settings__hint">
                        Поля и формулировки критериев настраиваются для компании. Текстовая характеристика рабочего обязательна; общая причина её не заменяет. Для ИТР характеристика необязательна.
                    </p>
                    <div class="timesheet-settings__characteristics">
                        <article
                            v-for="(field, fieldIndex) in form.policy_config.characteristic_fields"
                            :key="field.key"
                        >
                            <b-form-input
                                v-model.trim="field.label"
                                aria-label="Название характеристики"
                            ></b-form-input>
                            <b-form-select
                                v-model="field.type"
                                :options="characteristicTypeOptions"
                                aria-label="Тип характеристики"
                            ></b-form-select>
                            <b-form-checkbox v-model="field.required" :disabled="field.purpose === 'ktu_comment' || field.key === 'performance_note'" switch>
                                Обязательно
                            </b-form-checkbox>
                            <b-button
                                variant="outline-danger"
                                size="sm"
                                :aria-label="`Удалить характеристику ${field.label}`"
                                @click="removeCharacteristic(fieldIndex)"
                            >
                                <i class="fas fa-trash" aria-hidden="true"></i>
                            </b-button>
                        </article>
                    </div>
                    <b-button variant="outline-primary" size="sm" @click="addCharacteristic">
                        <i class="fas fa-plus mr-1" aria-hidden="true"></i>
                        Добавить характеристику
                    </b-button>

                    <label class="timesheet-settings__ktu-comment-field">
                        <span>Поле для фактического пояснения к низкому КТУ</span>
                        <b-form-select
                            v-model="ktuCommentFieldKey"
                            :options="ktuCommentFieldOptions"
                        ></b-form-select>
                    </label>

                    <div class="timesheet-settings__subheading">
                        <div>
                            <h3>Фактические основания КТУ</h3>
                            <p>Прораб видит только подходящие основания: положительные для 4,5–5 и отрицательные для 0–3,5.</p>
                        </div>
                        <b-button size="sm" variant="outline-primary" @click="addKtuCriterion">
                            <i class="fas fa-plus mr-1" aria-hidden="true"></i>
                            Добавить основание
                        </b-button>
                    </div>
                    <div class="timesheet-settings__criteria">
                        <article
                            v-for="(criterion, criterionIndex) in form.policy_config.ktu.criteria"
                            :key="criterion.key"
                        >
                            <b-form-select
                                v-model="criterion.direction"
                                :options="ktuDirectionOptions"
                                aria-label="Влияние критерия на КТУ"
                            ></b-form-select>
                            <b-form-input
                                v-model.trim="criterion.category"
                                placeholder="Раздел, например «Качество»"
                                aria-label="Раздел критерия"
                            ></b-form-input>
                            <b-form-input
                                v-model.trim="criterion.label"
                                placeholder="Наблюдаемый факт"
                                aria-label="Формулировка критерия"
                            ></b-form-input>
                            <b-form-checkbox
                                v-if="criterion.direction === 'negative'"
                                v-model="criterion.critical"
                            >
                                Критичное
                            </b-form-checkbox>
                            <span v-else class="timesheet-settings__criteria-spacer"></span>
                            <b-button
                                variant="outline-danger"
                                size="sm"
                                :aria-label="`Удалить основание ${criterion.label}`"
                                @click="removeKtuCriterion(criterionIndex)"
                            >
                                <i class="fas fa-trash" aria-hidden="true"></i>
                            </b-button>
                        </article>
                    </div>
                </section>
            </b-tab>

            <b-tab title="Сверка с отчётами">
                <section class="timesheet-settings__section">
                    <h2>Объекты без сверки с отчётами</h2>
                    <p class="timesheet-settings__hint">
                        Отметьте объекты, где ежесменные отчёты не ведутся или не должны
                        определять численность в табеле. Их табели можно отправлять,
                        утверждать и передавать в расчёт без совпадения с отчётами.
                        Изменение отчёта не вернёт такой табель на повторную проверку.
                        Остальные проверки табеля сохраняются.
                    </p>
                    <label>
                        <span>Найти объект</span>
                        <b-form-input v-model.trim="reconciliationQuery" type="search"
                                      placeholder="Название объекта"></b-form-input>
                    </label>
                    <p>Без сверки: {{ form.policy_config.report_reconciliation_exempt_object_ids.length }}</p>
                    <div class="timesheet-settings__object-list">
                        <b-form-checkbox
                            v-for="object in filteredReconciliationObjects"
                            :key="object.building_id"
                            v-model="form.policy_config.report_reconciliation_exempt_object_ids"
                            :value="Number(object.building_id)"
                        >
                            {{ object.name }}
                            <small v-if="!object.is_active" class="text-muted">(неактивный)</small>
                        </b-form-checkbox>
                        <p v-if="!filteredReconciliationObjects.length">Объекты не найдены.</p>
                    </div>
                    <p class="timesheet-settings__hint">
                        Настройка действует и для уже созданных табелей. Снятие отметки
                        возвращает обязательную сверку и снимает постоянное требование табеля.
                        Сохранённые ревизии не изменяются.
                    </p>
                </section>
            </b-tab>

            <b-tab title="Обязательные табели">
                <section class="timesheet-settings__section">
                    <h2>С каких объектов всегда ждать табель</h2>
                    <p class="timesheet-settings__hint">
                        Отметьте объекты, которые должны сдавать табель каждый месяц.
                        Настройка сохраняется для объекта: повторять выбор каждый месяц не нужно.
                        Пока обязательный табель не утверждён, месяц нельзя передать на расчёт зарплаты,
                        кроме месяцев, указанных на вкладке «Исключения из комплекта».
                    </p>
                    <p class="timesheet-settings__hint">
                        Здесь доступны только объекты с отключённой сверкой со сменными отчётами.
                        Объекты с отчётами и уже созданными табелями попадают в проверку месяца автоматически.
                        Неактивные объекты по этой настройке не ожидаются.
                    </p>
                    <label>
                        <span>Найти объект</span>
                        <b-form-input v-model.trim="requiredObjectQuery" type="search"
                                      placeholder="Название объекта"></b-form-input>
                    </label>
                    <div class="timesheet-settings__object-list">
                        <b-form-checkbox v-for="object in filteredRequiredObjects" :key="object.building_id"
                            v-model="form.policy_config.required_timesheet_object_ids" :value="Number(object.building_id)">
                            {{ object.name }}
                            <small v-if="!object.is_active" class="text-muted">(неактивный — табель не ожидается)</small>
                        </b-form-checkbox>
                        <p v-if="!filteredRequiredObjects.length">
                            Объекты не найдены. Отключить сверку можно на вкладке «Сверка с отчётами».
                        </p>
                    </div>
                </section>
            </b-tab>
            <b-tab title="Несколько табелей">
                <TimesheetMultipleSettings v-model="form.policy_config.multiple_timesheet_objects"
                    :objects="objects" :users="participantUsers"
                    :exempt-ids="form.policy_config.report_reconciliation_exempt_object_ids" />
            </b-tab>
            <b-tab title="Доступ">
                <b-alert show variant="secondary">
                    Администратор имеет полный доступ. Роль Top сама по себе прав не добавляет.
                </b-alert>
                <TimesheetObjectAccess
                    v-model="form.access_config.object_grants"
                    :objects="objects"
                    :users="participantUsers"
                />
                <section
                    v-for="right in scopedRights"
                    :key="right.key"
                    class="timesheet-settings__access"
                >
                    <h2>{{ right.title }}</h2>
                    <p>{{ right.description }}</p>
                    <div class="timesheet-settings__access-grid">
                        <article>
                            <h3>На всех объектах</h3>
                            <MultipleParticipantsConfig
                                :value="form.access_config[right.key].all_objects"
                                :users="participantUsers"
                                :departments="participantDepartments"
                                :allowed-types="baseAccessTypes"
                                :show-fallback-option="false"
                                :show-priority="false"
                                @input="value => setScoped(right.key, 'all_objects', value)"
                            />
                        </article>
                        <article>
                            <h3>На своих объектах</h3>
                            <MultipleParticipantsConfig
                                :value="form.access_config[right.key].own_objects"
                                :users="participantUsers"
                                :departments="participantDepartments"
                                :allowed-types="ownObjectAccessTypes"
                                :show-object-roles="true"
                                :object-role-options="objectRoleOptions"
                                :show-fallback-option="false"
                                :show-priority="false"
                                @input="value => setScoped(right.key, 'own_objects', value)"
                            />
                        </article>
                    </div>
                </section>

                <section
                    v-for="right in globalRights"
                    :key="right.key"
                    class="timesheet-settings__access"
                >
                    <h2>{{ right.title }}</h2>
                    <p>{{ right.description }}</p>
                    <MultipleParticipantsConfig
                        :value="form.access_config[right.key]"
                        :users="participantUsers"
                        :departments="participantDepartments"
                        :allowed-types="baseAccessTypes"
                        :show-fallback-option="false"
                        :show-priority="false"
                        @input="value => setGlobal(right.key, value)"
                    />
                </section>
            </b-tab>
        </b-tabs>
    </main>
</template>

<script>
import api from '@/utils/api';
import TimesheetPayrollBases from './TimesheetPayrollBases.vue';
import TimesheetObjectAccess from './TimesheetObjectAccess.vue';
import TimesheetMultipleSettings from './TimesheetMultipleSettings.vue';
import TimesheetSubmissionSettings from './TimesheetSubmissionSettings.vue';
import TimesheetPackageExclusions from './TimesheetPackageExclusions.vue';
import MultipleParticipantsConfig from '@/views/Requests/RequestTypes/MultipleParticipantsConfig/MultipleParticipantsConfig.vue';
import TimesheetEmployeeCardSettings from './TimesheetEmployeeCardSettings.vue';

const EMPTY = {participants: []};
const BASE_ACCESS_TYPES = ['all', 'specific_users', 'department'];
const OWN_OBJECT_ACCESS_TYPES = BASE_ACCESS_TYPES.concat('object_role');
const OBJECT_ROLE_OPTIONS = [
    {value: 'main_staff', text: 'Прораб'},
    {value: 'curator', text: 'Куратор'},
    {value: 'has_access', text: 'Имеющий доступ к объекту'}
];

function clone(value) {
    return JSON.parse(JSON.stringify(value));
}

function participant(value) {
    return value && Array.isArray(value.participants)
        ? clone(value)
        : clone(EMPTY);
}

function defaults() {
    return {
        access_config: {
            object_grants: [],
            view: {
                all_objects: clone(EMPTY),
                own_objects: {participants: [{type: 'object_role', roles: ['main_staff', 'curator', 'has_access']}]}
            },
            edit: {
                all_objects: clone(EMPTY),
                own_objects: {participants: [{type: 'object_role', roles: ['main_staff']}]}
            },
            submit: {
                all_objects: clone(EMPTY),
                own_objects: {participants: [{type: 'object_role', roles: ['main_staff']}]}
            },
            review: clone(EMPTY),
            import_history: clone(EMPTY),
            resolve_history_people: clone(EMPTY),
            manage_settings: clone(EMPTY),
            view_payroll: clone(EMPTY),
            view_payroll_calculation: clone(EMPTY),
            manage_payroll: clone(EMPTY),
            pay_salary: clone(EMPTY)
        },
        policy_config: {
            text_quality_enabled: false,
            employee_card_responsibles: {participants: []},
            submission_deadline: {enabled: false, day: 3, rules: [{from_month: '2026-09', day: 3}], overrides: []},
            time_step_minutes: 15,
            normal_daily_minutes: 720,
            maximum_daily_minutes: 1440,
            allow_unassigned_employee: true,
            allow_unresolved_person: true,
            itr_department_ids: [],
            report_reconciliation_exempt_object_ids: [],
            required_timesheet_object_ids: [],
            payroll_package_exclusions: [],
            multiple_timesheet_objects: [],
            absence_code_mapping: {
                labor_leave: {enabled: true, code: 'О'},
                unpaid_leave: {enabled: true, code: 'А'},
                sick_leave: {enabled: true, code: 'Б'},
                state_duties: {enabled: true, code: 'Г'},
                absence: {enabled: true, code: 'П'}
            },
            day_counting: {
                full_day_minimum_minutes: 300,
                partial_day_value: 0.5,
                travel_only_day_value: 0.5,
                travel_day_codes: ['Д']
            },
            ktu: {
                enabled: true,
                minimum: 0,
                maximum: 5,
                step: 0.5,
                neutral: 4,
                critical_below: 3,
                criteria: []
            },
            characteristic_fields: [
                {key: 'business_trip_readiness', label: 'Готовность к командировкам', type: 'boolean', required: true},
                {key: 'learning_ability', label: 'Обучаемость', type: 'boolean', required: true},
                {key: 'responsibility', label: 'Ответственность и исполнительность', type: 'boolean', required: true},
                {key: 'performance_note', label: 'Особые заслуги / характеристика новичка', type: 'text', max_length: 4000, required: true, purpose: 'ktu_comment'}
            ]
        },
        rollout_config: {
            full_timesheet_start_date: '2026-09-01',
            pilot_object_ids: []
        }
    };
}

function normalize(value) {
    const result = defaults();
    const source = value || {};
    result.access_config.object_grants = clone(
        source.access_config && Array.isArray(source.access_config.object_grants)
            ? source.access_config.object_grants : []
    );
    ['view', 'edit', 'submit'].forEach(key => {
        if (source.access_config && source.access_config[key]) {
            result.access_config[key].all_objects = participant(source.access_config[key].all_objects);
            result.access_config[key].own_objects = participant(source.access_config[key].own_objects);
        }
    });
    ['review', 'import_history', 'resolve_history_people', 'manage_settings', 'view_payroll', 'view_payroll_calculation', 'manage_payroll', 'pay_salary'].forEach(key => {
        if (source.access_config && source.access_config[key]) {
            result.access_config[key] = participant(source.access_config[key]);
        }
    });
    result.policy_config = Object.assign(result.policy_config, source.policy_config || {});
    result.policy_config.ktu = Object.assign({criteria: []}, result.policy_config.ktu || {}, {
        enabled: true,
        minimum: 0,
        maximum: 5,
        step: 0.5,
        neutral: 4,
        critical_below: 3
    });
    result.policy_config.ktu.criteria = Array.isArray(result.policy_config.ktu.criteria)
        ? result.policy_config.ktu.criteria
        : [];
    result.policy_config.characteristic_fields = (result.policy_config.characteristic_fields || [])
        .map(field => Object.assign({required: true}, field));
    result.policy_config.itr_department_ids = (result.policy_config.itr_department_ids || [])
        .map(Number)
        .filter(Boolean);
    result.rollout_config = Object.assign(result.rollout_config, source.rollout_config || {});
    result.policy_config.report_reconciliation_exempt_object_ids =
        (result.policy_config.report_reconciliation_exempt_object_ids || []).map(Number).filter(Boolean);
    result.policy_config.required_timesheet_object_ids =
        (result.policy_config.required_timesheet_object_ids || []).map(Number).filter(id =>
            result.policy_config.report_reconciliation_exempt_object_ids.includes(id));
    result.rollout_config.pilot_object_ids = (result.rollout_config.pilot_object_ids || []).map(Number);
    return result;
}

export default {
    name: 'TimesheetSettings',
    components: {TimesheetEmployeeCardSettings, TimesheetPayrollBases, MultipleParticipantsConfig, TimesheetObjectAccess, TimesheetMultipleSettings, TimesheetSubmissionSettings, TimesheetPackageExclusions},
    data() {
        return {
            form: defaults(),
            objects: [],
            settingsUsers: [],
            codes: [],
            loading: true,
            saving: false,
            hasLoaded: false,
            accessDenied: false,
            errorMessage: '',
            successMessage: '',
            reconciliationQuery: '',
            requiredObjectQuery: '',
            travelDayCodesText: 'Д',
            baseAccessTypes: BASE_ACCESS_TYPES,
            ownObjectAccessTypes: OWN_OBJECT_ACCESS_TYPES,
            objectRoleOptions: OBJECT_ROLE_OPTIONS,
            characteristicTypeOptions: [
                {value: 'boolean', text: 'Да / нет'},
                {value: 'text', text: 'Текст'}
            ],
            ktuDirectionOptions: [
                {value: 'positive', text: 'Повышает КТУ'},
                {value: 'negative', text: 'Снижает КТУ'}
            ],
            absenceTypes: [
                {key: 'labor_leave', label: 'Трудовой отпуск'},
                {key: 'unpaid_leave', label: 'Отпуск без сохранения'},
                {key: 'sick_leave', label: 'Больничный'},
                {key: 'state_duties', label: 'Государственные обязанности'},
                {key: 'absence', label: 'Прогул / неявка'}
            ],
            scopedRights: [
                {key: 'view', title: 'Просмотр табелей и свода', description: 'Кто может открывать табели, статусы подачи и свод по сотрудникам в пределах доступных объектов. Для просмотра кураторами своих объектов выберите роль «Куратор» в настройке своих объектов. Просмотр не даёт права проверки или изменения.'},
                {key: 'edit', title: 'Заполнение', description: 'Кто может менять дневные отметки.'},
                {key: 'submit', title: 'Отправка', description: 'Кто может отправлять ревизию на проверку.'}
            ],
            globalRights: [
                {key: 'review', title: 'Проверка и утверждение табелей', description: 'Просмотр всех объектов, возврат, утверждение и разбор инцидентов. Если нужен только просмотр, в том числе кураторам по своим объектам, настройте блок «Просмотр табелей и свода» выше.'},
                {key: 'import_history', title: 'Импорт истории', description: 'Предпросмотр, сопоставление и применение старых табелей и начислений. Исходные зарплатные данные также требуют права просмотра расчёта и истории ЗП.'},
                {key: 'resolve_history_people', title: 'Сопоставление сотрудников архива', description: 'Связь перенесённых записей с карточками сотрудников. Исходная история не изменяется; суммы доступны только с правом просмотра зарплаты.'},
                {key: 'manage_settings', title: 'Настройки', description: 'Изменение этого экрана.'},
                {key: 'view_payroll', title: 'Просмотр зарплаты', description: 'Только итоговая ведомость после передачи бухгалтерии. Без ставок, формул и правок руководителя.'},
                {key: 'view_payroll_calculation', title: 'Просмотр расчёта и истории ЗП', description: 'Ставки, формулы, принятые дни и история изменений без редактирования. Не выдавайте бухгалтеру, которому нужны только выплаты. Право «Расчёт зарплаты» также открывает эти данные.'},
                {key: 'manage_payroll', title: 'Расчёт зарплаты', description: 'Ставки, расчёт, возврат табелей и передача бухгалтеру.'},
                {key: 'pay_salary', title: 'Выплата зарплаты', description: 'Итоговая ведомость, приём, фиксация выплат и возврат неоплаченных строк. Само по себе не открывает ставки, расчёт и изменения руководителя.'}
            ]
        };
    },
    computed: {
        absenceCodeOptions() {
            return [{value: '', text: 'Выберите код'}].concat(this.codes
                .filter(code => ['absence', 'day_off'].includes(code.category))
                .map(code => ({value: code.code, text: `${code.code} — ${code.name}`})));
        },
        filteredReconciliationObjects() {
            const query = this.reconciliationQuery.toLocaleLowerCase('ru-RU');
            return this.objects.filter(object => (
                !query || String(object.name || '').toLocaleLowerCase('ru-RU').includes(query)
            ));
        },
        filteredRequiredObjects() {
            const query = this.requiredObjectQuery.toLocaleLowerCase('ru-RU');
            return this.objects.filter(object => (
                this.form.policy_config.report_reconciliation_exempt_object_ids.includes(Number(object.building_id))
                && (!query || String(object.name || '').toLocaleLowerCase('ru-RU').includes(query))
            ));
        },
        participantUsers() {
            return this.settingsUsers;
        },
        participantDepartments() {
            return (this.$store.state.departments || []).filter(row => row.is_active !== false).map(row => ({
                value: Number(row.id || row.department_id),
                text: row.name
            })).filter(option => option.value);
        },
        ktuCommentFieldOptions() {
            return [{value: '', text: 'Выберите текстовое поле'}].concat(
                (this.form.policy_config.characteristic_fields || [])
                    .filter(field => field.type === 'text')
                    .map(field => ({value: field.key, text: field.label}))
            );
        },
        ktuCommentFieldKey: {
            get() {
                const field = (this.form.policy_config.characteristic_fields || [])
                    .find(item => item.purpose === 'ktu_comment');
                return field ? field.key : '';
            },
            set(value) {
                (this.form.policy_config.characteristic_fields || []).forEach(field => {
                    if (field.key === value) {
                        this.$set(field, 'purpose', 'ktu_comment');
                        this.$set(field, 'required', true);
                    }
                    else if (field.purpose === 'ktu_comment') this.$delete(field, 'purpose');
                });
            }
        }
    },
    watch: {
        'form.policy_config.report_reconciliation_exempt_object_ids'(ids) {
            this.form.policy_config.required_timesheet_object_ids =
                this.form.policy_config.required_timesheet_object_ids.filter(id => ids.includes(id));
        }
    },
    async mounted() {
        await this.load();
    },
    methods: {
        errorText(error, fallback) {
            const data = error && error.response && error.response.data;
            return data && data.msg ? data.msg : fallback;
        },
        async load() {
            this.loading = true;
            try {
                const responses = await Promise.all([
                    api.get('/api/timesheets/settings', {ignoreModal: true}),
                    api.get('/api/timesheets/bootstrap', {ignoreModal: true}),
                    this.$store.dispatch('getDepartments').catch(() => null)
                ]);
                this.form = normalize(responses[0].data);
                this.settingsUsers = responses[0].data.user_options || [];
                this.travelDayCodesText = (this.form.policy_config.day_counting.travel_day_codes || []).join(', ');
                this.objects = responses[1].data.objects || [];
                this.codes = responses[1].data.codes || [];
                this.hasLoaded = true;
            } catch (error) {
                if (error && error.response && error.response.status === 403) {
                    this.accessDenied = true;
                } else {
                    this.errorMessage = this.errorText(error, 'Не удалось загрузить настройки.');
                }
            } finally {
                this.loading = false;
            }
        },
        setScoped(right, scope, value) {
            this.$set(this.form.access_config[right], scope, participant(value));
        },
        applyTravelDayCodes() {
            this.form.policy_config.day_counting.travel_day_codes = this.travelDayCodesText
                .split(',')
                .map(code => code.trim())
                .filter(Boolean);
        },
        addCharacteristic() {
            const fields = this.form.policy_config.characteristic_fields;
            fields.push({
                key: `custom_${Date.now()}`,
                label: 'Новая характеристика',
                type: 'boolean',
                required: true
            });
        },
        removeCharacteristic(index) {
            this.form.policy_config.characteristic_fields.splice(index, 1);
        },
        addKtuCriterion() {
            this.form.policy_config.ktu.criteria.push({
                key: `criterion_${Date.now()}`,
                label: 'Новое фактическое основание',
                category: '',
                direction: 'positive',
                critical: false
            });
        },
        removeKtuCriterion(index) {
            this.form.policy_config.ktu.criteria.splice(index, 1);
        },
        setGlobal(right, value) {
            this.$set(this.form.access_config, right, participant(value));
        },
        async save() {
            if (this.form.policy_config.payroll_package_exclusions.some(row => !row.building_id || !row.from_month
                || row.through_month !== null && (!row.through_month || row.through_month < row.from_month))) {
                this.errorMessage = 'Выберите объект и месяцы исключения из комплекта. Последний месяц не может быть раньше первого.';
                return;
            }
            if (this.form.policy_config.multiple_timesheet_objects.some(row => !row.responsible_user_id)) {
                this.errorMessage = 'Выберите ответственного за основной табель каждого включённого объекта.';
                return;
            }
            this.applyTravelDayCodes();
            this.saving = true;
            this.errorMessage = '';
            this.successMessage = '';
            try {
                const response = await api.put('/api/timesheets/settings', normalize(this.form), {
                    ignoreModal: true
                });
                this.form = normalize(response.data);
                this.travelDayCodesText = (this.form.policy_config.day_counting.travel_day_codes || []).join(', ');
                this.successMessage = 'Настройки табелей сохранены.';
            } catch (error) {
                this.errorMessage = this.errorText(error, 'Не удалось сохранить настройки.');
            } finally {
                this.saving = false;
            }
        }
    }
};
</script>

<style scoped>
.timesheet-settings {
    max-width: 1280px;
    margin: 0 auto;
    padding: 1.25rem;
}

.timesheet-settings > header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 1rem;
}

.timesheet-settings h1,
.timesheet-settings h2,
.timesheet-settings h3 {
    margin-top: 0;
}

.timesheet-settings header p,
.timesheet-settings__access > p {
    color: #687888;
}

.timesheet-settings__loading {
    display: flex;
    justify-content: center;
    gap: .75rem;
    padding: 3rem;
}

.timesheet-settings__section,
.timesheet-settings__access {
    display: grid;
    gap: 1rem;
    padding: .75rem;
}

.timesheet-settings__section label > span {
    display: block;
    margin-bottom: .35rem;
    font-weight: 700;
}

.timesheet-settings__grid {
    grid-template-columns: repeat(3, minmax(180px, 1fr));
}

.timesheet-settings__grid .custom-checkbox {
    grid-column: 1 / -1;
}

.timesheet-settings__object-list {
    display: grid;
    max-height: 320px;
    gap: .4rem;
    padding: .75rem;
    overflow-y: auto;
    border: 1px solid #ced4da;
    border-radius: .25rem;
}

.timesheet-settings__department-list {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    max-height: 320px;
    gap: .5rem 1rem;
    padding: .85rem;
    overflow-y: auto;
    border: 1px solid #ced4da;
    border-radius: .5rem;
    background: #f8fafc;
}

.timesheet-settings__department-list p {
    margin: 0;
    color: #687888;
}

.timesheet-settings__absence-mapping {
    display: grid;
    gap: .65rem;
}

.timesheet-settings__absence-mapping article {
    display: grid;
    grid-template-columns: minmax(220px, 1fr) minmax(220px, 1fr);
    align-items: center;
    gap: .75rem;
}

.timesheet-settings__hint {
    margin: 0;
    color: #687888;
}

.timesheet-settings__characteristics {
    display: grid;
    gap: .5rem;
}

.timesheet-settings__characteristics article {
    display: grid;
    grid-template-columns: minmax(240px, 1fr) 180px 150px auto;
    align-items: center;
    gap: .5rem;
}

.timesheet-settings__ktu-comment-field {
    max-width: 620px;
}

.timesheet-settings__subheading {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 1rem;
    margin-top: .75rem;
}

.timesheet-settings__subheading h3,
.timesheet-settings__subheading p {
    margin: 0;
}

.timesheet-settings__subheading p {
    margin-top: .25rem;
    color: #687888;
    font-size: .85rem;
}

.timesheet-settings__criteria {
    display: grid;
    gap: .5rem;
}

.timesheet-settings__criteria article {
    display: grid;
    grid-template-columns: 165px 170px minmax(280px, 1fr) 130px auto;
    align-items: center;
    gap: .5rem;
    padding: .5rem;
    border: 1px solid #d9e2ec;
    border-radius: 8px;
    background: #fbfcfd;
}

.timesheet-settings__criteria-spacer {
    min-height: 1px;
}

.timesheet-settings__access {
    margin-bottom: 1rem;
    border: 1px solid #d9e2ec;
    border-radius: 12px;
}

.timesheet-settings__access-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
}

.timesheet-settings__access-grid article {
    padding: .75rem;
    border-radius: 10px;
    background: #f7f9fb;
}

@media (max-width: 760px) {
    .timesheet-settings__grid,
    .timesheet-settings__access-grid {
        grid-template-columns: 1fr;
    }

    .timesheet-settings__characteristics article {
        grid-template-columns: 1fr auto;
    }

    .timesheet-settings__characteristics article .custom-select {
        grid-column: 1 / -1;
        grid-row: 2;
    }

    .timesheet-settings__characteristics article .custom-control {
        grid-column: 1;
    }

    .timesheet-settings__subheading {
        align-items: stretch;
        flex-direction: column;
    }

    .timesheet-settings__criteria article {
        grid-template-columns: 1fr auto;
    }

    .timesheet-settings__criteria article .custom-select,
    .timesheet-settings__criteria article .form-control,
    .timesheet-settings__criteria article .custom-control {
        grid-column: 1 / -1;
    }

    .timesheet-settings__absence-mapping article {
        grid-template-columns: 1fr;
    }
}
</style>
